import { Alert, Box, Card, CardContent, Link, Stack, Typography } from '@mui/material';
import { t } from 'i18next';
import { BackButton } from '../../../../../common/components/BackButton';
import { ComponentLoader } from '../../../../../common/components/ComponentLoader';
import { FilterStatusBadge } from '../../../../../common/components/FilterStatusBadge';
import { SeverityBadge } from '../../../../../common/instances/components/SeverityBadge';
import { ExportButton } from '../../../../../exports/triggers/buttons/InstanceExportButton';
import { JiraIssue } from '../../../../../integrations/jira/models/valueObjects/JiraIssue';
import { Instance } from '../models/Instance';
import { RemediationDetails } from './RemediationDetails';
import { OverviewSkeleton } from './skeletons/OverviewSkeleton';
import { FeatureFlagsType, getFeatureFlag } from '../../../../../config/launch-darkly/FeatureFlagsController';
import { ExportType } from '../../../../../integrations/exports/models/BatchExport';

type Props = {
  instance: Instance | null;
  loading: boolean;
  initialLoad: boolean;
  includeFilteredItems: boolean;
  onJiraExport?: () => Promise<JiraIssue | null>;
  onError: (error: unknown) => void;
  jiraExportUrl: string | null;
  onServiceNowExport?: () => Promise<void>;
  serviceNowExportUrl: string | null;
};

const ExportLink = (props: { url: string | null; exportType: ExportType.JIRA | ExportType.SERVICE_NOW }) => {
  if (!props.url) return null;
  return (
    <Alert color="success" sx={{ '& .MuiAlert-message': { overflow: 'hidden' } }}>
      {props.exportType === ExportType.JIRA ? t('viewJiraIssue') : t('serviceNowExport.viewExportedRecord')}:
      <Link sx={{ ml: 1, overflowWrap: 'anywhere' }} href={props.url} target="_blank">
        {props.url}
      </Link>
    </Alert>
  );
};

export const Overview = ({
  instance,
  includeFilteredItems,
  loading,
  initialLoad,
  onJiraExport,
  onError,
  jiraExportUrl,
  onServiceNowExport,
  serviceNowExportUrl,
}: Props) => {
  if (initialLoad || !instance) {
    return <OverviewSkeleton />;
  }

  const issueNumber = instance.getRuleNumber();
  const severity = instance.getSeverity();
  const localNumber = instance.getLocalNumber();
  const filterStatus = instance.getFilterStatus();
  const data = instance.getData();
  const remediation = instance.getRemediation();
  const specificTag = instance.getSpecificRemediationTag();

  return (
    <ComponentLoader loading={loading}>
      <Card className="instance-overview">
        <CardContent>
          <Stack direction="row" gap={2}>
            <BackButton sx={{ display: { xl: 'none' } }} />
            <SeverityBadge severity={severity}>
              {issueNumber}.{localNumber}
            </SeverityBadge>
            <FilterStatusBadge format="large" filterStatus={filterStatus} />
            <Box sx={{ flexGrow: 1 }} />
            <ExportButton
              instance={instance}
              includeFilteredItems={includeFilteredItems}
              onError={onError}
              onJiraExport={onJiraExport ?? undefined}
              onServiceNowExport={onServiceNowExport}
            />
          </Stack>
          <Typography variant="h3" sx={{ pt: 3, fontSize: '1.75rem', wordBreak: 'break-word' }}>
            {data}
          </Typography>
          <Box sx={{ my: 2 }}>
            <ExportLink url={jiraExportUrl} exportType={ExportType.JIRA} />
          </Box>
          {serviceNowExportUrl && getFeatureFlag(FeatureFlagsType.ENABLE_SERVICE_NOW) && (
            <Box sx={{ my: 2 }}>
              <ExportLink url={serviceNowExportUrl} exportType={ExportType.SERVICE_NOW} />
            </Box>
          )}
          <RemediationDetails remediation={remediation} specificTag={specificTag} />
        </CardContent>
      </Card>
    </ComponentLoader>
  );
};
